import { webStorage } from '@/utils/web-storage';
import {
  S_CAR_MODELS,
  S_CHAT_CONFIG,
  S_COUNTRIES,
  S_GLOBAL_CONFIG,
  S_LANG,
  S_LANGUAGES,
  S_PROVINCE_LIST,
  S_SHIPPING_COUNTRIES,
  S_SITE_ERRORS,
  S_WEBSHOP_SHIPPING_COUNTRIES,
  S_STORE_TYPES,
} from '@/utils/web-storage-keys';
import api from '@/api';
import config from '@/temp/config';
import { isNoE } from '@/utils/obj-utils';
import { formatDate } from '@/utils/date-utils';
import { eq, ifEmpty } from '@/utils/string-utils';
import { getLangFromUrl } from '@/utils/uri-utils';
import { getCurrentAlpha2Code, getGlobalConfigs } from '@/utils/site-utils';
import { sortByChar } from '@/utils/array-utils';
import { S_REGISTRABLE_COUNTRIES } from '../utils/web-storage-keys';

export const getSiteError = async (ex) => {
  const errors = (await getSiteErrors()) ?? [];
  let error = null;
  if (ex?.message === 'Network Error') {
    error = errors.find((x) => x.code === 'network_error');
  } else {
    if (ex?.code) {
      error = errors.find((x) => x.code == ex?.code);
    }
    if (!error) {
      error = errors.find((x) => x.code == ex);
    }
    if (!error) {
      error = errors.find((x) => x.code === 'default_error');
    }
  }
  return error?.message ?? ex?.message ?? 'Uncertain Error!';
};
export const getSiteErrors = async () => {
  const lang = webStorage.get(S_LANG);
  let cache = webStorage.get(S_SITE_ERRORS) ?? {};
  if (!cache[lang]) {
    const [res, ex] = await api.web.item({
      id: config.app.web.itemIds.apiErrors,
      isDeep: true,
    });
    if (res?.children) {
      cache[lang] = res.children;
      webStorage.set(S_SITE_ERRORS, cache);
    }
  }
  return cache[lang] ?? [];
};
const getScCountries = async (cacheKey, rootId) => {
  const lang = getLangFromUrl();
  let cache = webStorage.get(cacheKey) ?? {};
  if (isNoE(cache[lang])) {
    const [res, ex] = await api.web.item({
      id: rootId,
      isDeep: true,
    });
    if (ex) {
      return [];
    }
    cache[lang] = res?.children?.filter((x) => !!x.alpha2Code) ?? [];
    webStorage.set(S_SHIPPING_COUNTRIES, cache);
  }
  if (isNoE(cache[lang])) return [];
  return cache[lang] ?? [];
};
export const getEncryptor = () => {
  const CryptoJS = require('crypto-js');
  return {
    async encrypt(str) {
      if (isNoE(str)) return;
      const [res, ex] = await api.cidp.getSecretByType();
      if (ex) {
        return [null, ex];
      }
      const { iv, secret } = res;
      const _key = CryptoJS.enc.Utf8.parse(secret);
      const _iv = CryptoJS.enc.Utf8.parse(iv);
      const _str = CryptoJS.SHA256(str).toString();
      const encryptedStr = CryptoJS.AES.encrypt(_str, _key, {
        iv: _iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      return [encryptedStr.toString(), null];
    },
  };
};
/**
 *
 * @returns {Promise<ChatConfiguration>}
 */
export const getChatConfiguration = async () => {
  let result = webStorage.get(S_CHAT_CONFIG);
  if (!result) {
    const [res, ex] = await api.web.item({
      id: config.app.web.itemIds.chatConfig,
    });
    if (ex) {
      return [];
    }
    result = res;
    webStorage.set(S_CHAT_CONFIG, result);
  }
  return result;
};

export const getRemoteGlobalConfig = async () => {
  let result = webStorage.get(S_GLOBAL_CONFIG);
  if (!result) {
    const [res, ex] = await api.web.item({
      id: config?.app?.web?.itemIds?.globalConfig,
    });
    if (ex) {
      return [];
    }
    result = res;
    webStorage.set(S_GLOBAL_CONFIG, result);
  }
  return result;
};
export const getLanguages = async () => {
  const lang = getLangFromUrl();
  let cache = webStorage.get(S_LANGUAGES) || {};
  if (!cache[lang]?.length) {
    const [res, ex] = await api.web.item({
      id: config.app.web.itemIds.languages,
      isDeep: true,
    });
    if (ex) {
      return [];
    }
    cache[lang] = res?.children ?? [];
  }
  return cache[lang];
};
export const getLangOptions = async () => {
  const list = await getLanguages();
  const options = list.map((x) => ({
    code: x.code,
    text: ifEmpty(x.fullText, x.text, x.code),
    data: x,
  }));
  sortByChar(options, (x) => x.text);
  return options;
};
export const getMarketLangOptions = async () => {
  const alpha2Code = getCurrentAlpha2Code();
  const list = await getLangOptions();
  return list.filter((x) => new RegExp(`-${alpha2Code}$`, 'gi').test(x.code));
};
